import type { StructuredOutput } from "../../../api/threads/liveMessages";
import type { HumanInputRequest } from "../../../api/threads/threadsApi";
import { isJsonObject } from "../../../api/http/jsonDecoding";
import { jcsDigest } from "../../../shared/formatting/jcsDigest";
import type { AcceptedChoice } from "../datasets/acceptedResults";
import { includedMembers } from "../datasets/acceptedResults";

/**
 * The release request of the Workflow "Release the knowledge data" (ADR-0026 slice 3b), as the
 * server's `release_request` value holds it: each person step fills its part, what, metadata
 * or where, and answers with the whole request; a part not chosen yet is `null`.
 */

export const RELEASE_PARTS = ["what", "metadata", "where"] as const;
export type ReleasePart = (typeof RELEASE_PARTS)[number];

export interface ReleaseMetadata {
  readonly title: string;
  readonly licence: string;
  readonly description: string;
  readonly keywords: readonly string[];
}

export interface ReleaseRequest {
  readonly model_version: string;
  readonly members: readonly string[];
  readonly expected_latest_version: number;
  readonly metadata: ReleaseMetadata | null;
  readonly publish_to_researchspace: boolean | null;
}

/** What the part forms edit before it becomes a request. */
export interface ReleaseDraft {
  /** The chosen model version's commit. */
  readonly modelVersion: string | null;
  /** Accepted results whose inclusion differs from their default. */
  readonly overrides: ReadonlyMap<string, boolean>;
  readonly title: string;
  readonly licence: string;
  readonly description: string;
  readonly toResearchSpace: boolean;
}

/** Whether a part's own fields are complete, so its answer can be sent. */
export function partComplete(part: ReleasePart, draft: ReleaseDraft, members: readonly string[]): boolean {
  switch (part) {
    case "what":
      return draft.modelVersion !== null && members.length > 0;
    case "metadata":
      return draft.title.trim() !== "" && draft.licence.trim() !== "";
    case "where":
      return true;
  }
}

/** The request so far, from the value a step's input holds; `null` if it holds none. */
export function readReleaseRequest(output: StructuredOutput | HumanInputRequest["result"] | null | undefined): ReleaseRequest | null {
  if (output?.output_kind !== "value" || output.value_kind !== "release_request" || !isJsonObject(output.value)) return null;
  const { model_version: modelVersion, members, expected_latest_version: expected, metadata, publish_to_researchspace: publish } = output.value;
  if (typeof modelVersion !== "string" || !Array.isArray(members) || typeof expected !== "number") return null;
  return {
    model_version: modelVersion,
    members: members.filter((member): member is string => typeof member === "string"),
    expected_latest_version: expected,
    metadata: readMetadata(metadata),
    publish_to_researchspace: typeof publish === "boolean" ? publish : null,
  };
}

function readMetadata(value: unknown): ReleaseMetadata | null {
  if (!isJsonObject(value)) return null;
  const { title, licence, description, keywords } = value;
  if (typeof title !== "string" || typeof licence !== "string" || typeof description !== "string") return null;
  return { title, licence, description, keywords: Array.isArray(keywords) ? keywords.filter((keyword): keyword is string => typeof keyword === "string") : [] };
}

/**
 * The request a part answers with: the request so far, kept as it is, with this part filled
 * from the draft. Only "what" starts one; it records the release number the members were
 * chosen against, so a release made meanwhile refuses this one.
 */
export function requestWithPart(
  part: ReleasePart,
  draft: ReleaseDraft,
  choices: readonly AcceptedChoice[],
  earlier: ReleaseRequest | null,
  latestVersion: number,
  keywords: readonly string[],
): ReleaseRequest | null {
  if (part === "what") {
    if (draft.modelVersion === null) return null;
    return { model_version: draft.modelVersion, members: includedMembers(choices, draft.overrides), expected_latest_version: latestVersion, metadata: null, publish_to_researchspace: null };
  }
  if (earlier === null) return null;
  if (part === "metadata") {
    return { ...earlier, metadata: { title: draft.title.trim(), licence: draft.licence.trim(), description: draft.description.trim(), keywords } };
  }
  return { ...earlier, publish_to_researchspace: draft.toResearchSpace };
}

/** The request as a step's answer: a `release_request` value with its digest. */
export async function releaseRequestOutput(request: ReleaseRequest): Promise<HumanInputRequest["result"]> {
  return { output_kind: "value", value_kind: "release_request", value: { ...request, members: [...request.members] }, digest: await jcsDigest(request) };
}
